import { memo, useEffect, useMemo, useState } from 'react'
import { itemsApi, programsApi, type RelatedEdition, type RelatedEditionsResult } from '../../lib/api.js'
import type { ScheduleItem } from '../../types/index.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import './AgendaDialogs.css'

function formatDate(value: string | null) {
  if (!value) return null
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(new Date(value))
}

function relationLabel(program: RelatedEdition, source: RelatedEditionsResult['source']) {
  if (source.series && program.series?.id === source.series.id) return `Série · ${source.series.title}`
  const shared = program.collections.find(collection => source.collections.some(value => value.id === collection.id))
  return shared ? `Collection · ${shared.name}` : 'Édition liée'
}

export const RelatedEditionDialog = memo(function RelatedEditionDialog(props: {
  item: ScheduleItem
  onSaved: (item: ScheduleItem) => void
  onClose: () => void
}) {
  const [result, setResult] = useState<RelatedEditionsResult | null>(null)
  const [query, setQuery] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!props.item.programId) return
    programsApi.listRelatedEditions(props.item.programId)
      .then(setResult)
      .catch(cause => setError(cause?.message ?? 'Impossible de charger les éditions liées.'))
  }, [props.item.programId])

  const programs = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('fr-FR')
    if (!normalized) return result?.programs ?? []
    return (result?.programs ?? []).filter(program => (
      program.title.toLocaleLowerCase('fr-FR').includes(normalized) ||
      program.series?.title.toLocaleLowerCase('fr-FR').includes(normalized) ||
      program.collections.some(collection => collection.name.toLocaleLowerCase('fr-FR').includes(normalized))
    ))
  }, [query, result])

  const replace = async (program: RelatedEdition) => {
    setBusyId(program.id)
    setError(null)
    try {
      const updated = await recordGridChange({
        label:   `remplacement par ${program.title}`,
        gridIds: [props.item.gridId],
        date:    broadcastDayKey(props.item.startTime),
      }, () => itemsApi.replaceProgram(props.item.id, program.id))
      props.onSaved(updated)
      props.onClose()
    } catch (cause: any) {
      setError(cause?.message ?? 'Impossible de remplacer cette édition.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <ModalShell title="Remplacer par une édition liée" onClose={props.onClose} size="medium" className="agenda-dialog agenda-dialog--editions">
      <div className="agenda-dialog__subtitle">{props.item.program?.title} · l’heure de début reste inchangée</div>

      <input
        autoFocus
        className="tp-input agenda-dialog__search"
        placeholder="Rechercher dans la série ou les collections…"
        value={query}
        onChange={event => setQuery(event.target.value)}
      />

      {error && <div className="agenda-dialog__error agenda-dialog__error--boxed" role="alert">{error}</div>}

      <div className="agenda-dialog__results">
        {!result && !error && <div className="agenda-dialog__empty"><Spinner /> Chargement des éditions liées…</div>}
        {result && programs.length === 0 && <div className="agenda-dialog__empty">
          {query ? 'Aucune édition ne correspond à la recherche.' : 'Aucune autre édition active dans la même série ou collection.'}
        </div>}
        {result && programs.map(program => {
          const premiere = program.isNew && !program.firstAiredAt && program.legacyRerunCount === 0
          const planned = formatDate(program.plannedFirstAirDate)
          return <div key={program.id} className="agenda-dialog__result-row">
            <div className="agenda-dialog__result-main">
              <div className="agenda-dialog__result-title">{program.title}</div>
              <div className="agenda-dialog__result-meta">
                {relationLabel(program, result.source)} · {premiere ? 'Inédit' : 'Rediffusion'}
                {planned ? ` · prévue le ${planned}` : ''} · {Math.round(program.durationSec / 60)} min
              </div>
              {!program.hasUsableMedia && <div className="agenda-dialog__warning">Média pas encore disponible</div>}
            </div>
            <button
              className="tp-button tp-button--primary"
              disabled={busyId !== null}
              onClick={() => { replace(program).catch(console.error) }}
            >
              {busyId === program.id ? 'Remplacement…' : 'Remplacer'}
            </button>
          </div>
        })}
      </div>
    </ModalShell>
  )
})
